import { ProgressIndicator } from 'components/progressIndicator/progressIndicator';
import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';
import { useSelector } from 'react-redux';
import { selectAccessibilityData } from 'reducers/accessibility';

import AssessmentLanguageSelector, { LanguageOption, LanguageOptions } from '../AssestmentLanguageSelector/AssestmentLanguageSelector';
import { WorkflowTemplate } from '../ViewApplication';
import { ViewApplicationStagesDetailsFooter } from '../ViewApplicationStagesDetailsFooter';

import { getBarClassName, getHelpIconText, getRequirementsIcons } from './service';
import { Toast } from 'components/toastMessages/toast';
import { Button } from 'components/button/Button';
import { Tooltip } from 'components/helpers/Tooltip';

import InformationLogo from 'assets/apply.clevry/Icon_Label_MoreInfo.png';

interface AssestmentCardProps {
    index: number,
    isDisabled: boolean,
    isCompleted: boolean,
    template: WorkflowTemplate
    handleStart: any;
    cancelBooking: (booking: { eventId: number }) => Promise<any>;
    languageOptions?: LanguageOptions;
}

const AssestmentCard: React.FC<AssestmentCardProps> = (props) => {

    const {
        index,
        isDisabled,
        isCompleted,
        template,
        languageOptions,
        handleStart,
        cancelBooking,
    } = props;

    const { t } = useTranslation(
        ['View+Application+Stages', 'View+Application+Stages+Details+Footer'],
        { i18n }
    );

    const isBookButtonDisabled =
        isDisabled ||
        (!template.hasBooking &&
            template.isBookable);

    let isDisabledFinal =
        template.isCompleted === true ? true : isDisabled;

    const [selectedAssesstmentLanguage, setSelectedAssesstmentLanguage] = React.useState<LanguageOption | undefined>();

    const accessibilityData = useSelector(selectAccessibilityData);

    const helpTooltipText = useMemo(() => {
        return getHelpIconText(template.prongCode);
    }, [template.prongCode]);

    return (
        <div
            className={`ViewApplicationSummaryWrapper ${isDisabledFinal ? `Disabled` : ''
                } ${isCompleted ? `Completed` : ''}`}
        >
            <div
                key={index}
                aria-disabled={isDisabledFinal || isCompleted}
                className={`ViewApplicationSummary ${isDisabledFinal ? `Disabled` : ''
                    } ${isCompleted ? `Completed` : ''}`}
            >
                <div className="ViewApplicationStageTemplateInner">
                    <div className="Progress">
                        <ProgressIndicator
                            key={index}
                            strokeColor={getBarClassName(
                                template.percentageCompletion,
                                template.isCompleted
                            )}
                            value={template.percentageCompletion}
                            overrideText={
                                isCompleted
                                    ? t('SSCandidate_View_Application_Stages_Done')
                                    : template.isBookable && !template.hasBooking
                                        ? t(
                                            'SSCandidate_View_Application_Stages_Ready_To_Book'
                                        )
                                        : isDisabledFinal
                                            ? ''
                                            : template.hasActiveReasonableAdjustmentRequest === true
                                                ? t(
                                                    'SSCandidate_View_Application_Stages_Pending'
                                                )
                                                : t(
                                                    'SSCandidate_View_Application_Stages_Ready_To_Start'
                                                )
                            }
                            total={100}
                            radius={50}
                            fill={getBarClassName(
                                template.percentageCompletion,
                                template.isCompleted
                            )}
                        />
                    </div>

                    <div
                        className={`ViewApplicationTemplateDetails ${isCompleted && 'titleOnlyDetails'
                            }`}
                    >
                        <div
                            className={`ViewApplicationTemplateHeading ${isCompleted && 'titleOnly'
                                }`}
                        >
                            <div className="InfoBlock">
                                <h3>{template.templateName}</h3>
                                {template.hasReusedExistingResult && (
                                    <p className="CompleteInfo">
                                        {t(
                                            'SSCandidate_View_Application_Stages_Complete_Info'
                                        )}
                                    </p>
                                )}
                                {isBookButtonDisabled && (
                                    <p className="CompleteInfo">
                                        {t(
                                            'SSCandidate_View_Application_Stages_No_Bookable_Slots'
                                        )}
                                    </p>
                                )}
                            </div>
                            {!isCompleted && helpTooltipText && (
                                <Tooltip
                                    text={helpTooltipText}
                                    image={InformationLogo}
                                />
                            )}
                        </div>
                        {!isCompleted && (
                            <div className="ViewApplicationRequirementsContainer">
                                {template.requirements && (
                                    <>
                                        <span>
                                            {t(
                                                'SSCandidate_View_Application_Stages_Requirements'
                                            )}
                                            :{' '}
                                        </span>
                                        {getRequirementsIcons(template.requirements, accessibilityData)}
                                    </>
                                )}
                            </div>
                        )}
                        {languageOptions && !isCompleted &&
                            <AssessmentLanguageSelector
                                languageOptions={languageOptions}
                                selectedLanguageControlled={selectedAssesstmentLanguage}
                                onSelectLanguage={(language) => setSelectedAssesstmentLanguage(language)}
                            />
                        }
                        {isCompleted && (
                            <h3 className="CompleteLabel">Completed</h3>
                        )}
                    </div>
                </div>
                {(template.hasActiveReasonableAdjustmentRequest ||
                    template.hasReasonableAdjustmentApproved ||
                    template.hasReasonableAdjustmentDeclined) && (
                        <div className="ViewApplicationAdjustmentContainer">
                            <span>Reasonable adjustment request:</span>
                            <span>
                                {template.hasReasonableAdjustmentApproved
                                    ? 'approved'
                                    : template.hasReasonableAdjustmentDeclined
                                        ? 'declined'
                                        : 'pending'}
                            </span>
                        </div>
                    )}
                <div className={`Buttons`}>
                    {!isCompleted && (
                        <Button
                            key={index}
                            priority="tertiary"
                            disabled={
                                isDisabledFinal ||
                                isBookButtonDisabled ||
                                template.hasActiveReasonableAdjustmentRequest === true ||
                                (languageOptions && !selectedAssesstmentLanguage)
                            }
                            onClick={() => {
                                handleStart(template, selectedAssesstmentLanguage?.value);
                            }}
                        >
                            {template.isBookable && !template.hasBooking
                                ? t('SSCandidate_View_Application_Stages_Book')
                                : t('SSCandidate_View_Application_Stages_Start')}
                        </Button>
                    )}
                </div>
            </div>
            {/* REPLACE WITH ACTUAL DATA */}
            {template.isBookable &&
                template.hasBooking &&
                !template.isCompleted ? (
                <ViewApplicationStagesDetailsFooter
                    time={format(new Date(template.booking.startDate), 'p')}
                    date={new Date(
                        template.booking.startDate
                    ).toLocaleDateString()}
                    Button2OnClick={() => {
                        (async () => {
                            const result = await cancelBooking({
                                eventId: template.booking.bookingID,
                            });
                            if (result.response) {
                                Toast.openToastSuccessMessage(
                                    t(
                                        'SSCandidate_View_Application_Stages_Booking_Cancelled'
                                    ),
                                    {
                                        className: 'ActivityNotificationToast',
                                    }
                                );
                                window.location.reload();
                            } else {
                                Toast.openToastErrorMessage(
                                    t(
                                        'SSCandidate_View_Application_Stages_Something_Went_Wrong'
                                    ),
                                    { className: 'ActivityNotificationToast' }
                                );
                            }
                        })();
                    }}
                    Button2Text={t(
                        'SSCandidate_View_Application_Stages_Details_Footer_Cancel_Booking',
                        { ns: 'View+Application+Stages+Details+Footer' }
                    )}
                    Button1OnClick={() => {
                        (async () => {
                            const result = await cancelBooking({
                                eventId: template.booking.bookingID,
                            });
                            if (result.response) {
                                Toast.openToastSuccessMessage(
                                    t(
                                        'SSCandidate_View_Application_Stages_Booking_Update'
                                    ),
                                    {
                                        onClose: () => {
                                            handleStart({
                                                ...template,
                                                hasBooking: false,
                                            }, selectedAssesstmentLanguage?.value);
                                        },
                                        className: 'ActivityNotificationToast',
                                    }
                                );
                            } else {
                                Toast.openToastErrorMessage(
                                    t(
                                        'SSCandidate_View_Application_Stages_Something_Went_Wrong'
                                    ),
                                    { className: 'ActivityNotificationToast' }
                                );
                            }
                        })();
                    }}
                    Button1Text={t(
                        'SSCandidate_View_Application_Stages_Details_Footer_Update_Booking',
                        { ns: 'View+Application+Stages+Details+Footer' }
                    )}
                />
            ) : (
                ''
            )}
        </div>
    );
};

export default AssestmentCard;